// План продаж на месяц по дням (Алексей 27.10 #2986/#2987): главный экран плана — таблица как в Excel,
// строки — позиции, столбцы — дни месяца, в ячейке «сколько продать в этот день». Блок из Excel
// вставляется целиком (Ctrl+V в любую ячейку или «Вставить из Excel»). Ячейка = цель (kind target)
// с on_date; пустая ячейка — цели на день нет (или действует «каждый день» из колонки «Обычно»).
import React, { useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ClipboardPaste, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { SalesPlanItemDto } from '@/api/salesPlan';
import { Tooltip } from '@/components/ui/tooltip';

export type GridRow = SalesPlanItemDto & { key: string };

const WD = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
const norm = (s: string) => s.trim().toLowerCase();
const num = (s: string) => {
  const v = Number(s.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(v) && v > 0 ? Math.round(v) : null;
};

export function MonthPlanGrid({ rows, disabled, onChange, newKey }: {
  rows: GridRow[];
  disabled: boolean;
  onChange: (fn: (rs: GridRow[]) => GridRow[]) => void;
  newKey: () => string;
}) {
  const now = new Date();
  const [ym, setYm] = useState<[number, number]>([now.getFullYear(), now.getMonth()]);
  const [y, m] = ym;
  const nDays = new Date(y, m + 1, 0).getDate();
  const days = Array.from({ length: nDays }, (_, i) => i + 1);
  const prefix = `${y}-${pad(m + 1)}-`;
  const todayIso = iso(now.getFullYear(), now.getMonth(), now.getDate());
  const [extra, setExtra] = useState<string[]>([]);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const gridRef = useRef<HTMLTableElement>(null);

  const targets = rows.filter((r) => r.kind === 'target');
  // Позиции месяца: всё, у чего есть цель в этом месяце или «каждый день», плюс добавленные вручную.
  const titles = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of targets) {
      if (!r.title.trim()) continue;
      if (r.on_date ? r.on_date.startsWith(prefix) : true) if (!seen.has(norm(r.title))) seen.set(norm(r.title), r.title);
    }
    for (const t of extra) if (!seen.has(norm(t))) seen.set(norm(t), t);
    return [...seen.values()];
  }, [targets, extra, prefix]);

  const cell = (title: string, d: string) => targets.find((r) => r.on_date === d && norm(r.title) === norm(title));
  const regular = (title: string) => targets.find((r) => !r.on_date && r.weekdays_mask === 127 && norm(r.title) === norm(title));

  /** Записать набор ячеек одной операцией: [title, date|null(=Обычно), qty|null]. */
  function write(cells: [string, string | null, number | null][]) {
    onChange((rs) => {
      let out = [...rs];
      for (const [title, d, qty] of cells) {
        const idx = out.findIndex((r) => r.kind === 'target' && norm(r.title) === norm(title)
          && (d ? r.on_date === d : !r.on_date && r.weekdays_mask === 127));
        if (qty == null) { if (idx >= 0) out = out.filter((_, i) => i !== idx); continue; }
        if (idx >= 0) out[idx] = { ...out[idx], target_qty: qty };
        else out.push({ key: newKey(), kind: 'target', title, time_from: null, time_to: null, weekdays_mask: 127, on_date: d, target_qty: qty });
      }
      return out;
    });
  }

  function rename(old: string, title: string) {
    setExtra((xs) => xs.map((x) => (x === old ? title : x)));
    onChange((rs) => rs.map((r) => (r.kind === 'target' && norm(r.title) === norm(old) && (!r.on_date || r.on_date.startsWith(prefix)) ? { ...r, title } : r)));
  }

  function removeTitle(title: string) {
    setExtra((xs) => xs.filter((x) => norm(x) !== norm(title)));
    onChange((rs) => rs.filter((r) => !(r.kind === 'target' && norm(r.title) === norm(title) && (!r.on_date || r.on_date.startsWith(prefix)))));
  }

  /** Блок TSV из Excel. col 0 = название позиции, col 1 = «Обычно», col 1+d = день d. */
  function pasteBlock(text: string, row0: number, col0: number, keepRegular = false) {
    const lines = text.replace(/\r/g, '').split('\n').filter((l) => l.length);
    const cells: [string, string | null, number | null][] = [];
    const newTitles: string[] = [];
    let ri = row0;
    for (const line of lines) {
      const vals = line.split('\t');
      let title = titles[ri];
      let c = col0;
      let k = 0;
      if (c === 0) {
        const t = (vals[0] ?? '').trim();
        // шапка Excel («Позиция ⇥ 1 ⇥ 2 …») — пропускаем
        const rest = vals.slice(1).map((v) => v.trim()).filter(Boolean);
        if (!t || /^(позиция|товар|наименование)/i.test(t) || rest.slice(0, 3).join(',') === '1,2,3') continue;
        title = t;
        if (!titles.some((x) => norm(x) === norm(t)) && !newTitles.some((x) => norm(x) === norm(t))) newTitles.push(t);
        k = 1; c = 1;
      }
      if (!title) { ri++; continue; }
      for (; k < vals.length; k++, c++) {
        if (c === 1) { if (!keepRegular) cells.push([title, null, num(vals[k])]); }
        else if (c - 1 <= nDays) cells.push([title, iso(y, m, c - 1), num(vals[k])]);
      }
      ri++;
    }
    if (newTitles.length) setExtra((xs) => [...xs, ...newTitles]);
    write(cells);
  }

  const shift = (dm: number) => setYm(([yy, mm]) => { const d = new Date(yy, mm + dm, 1); return [d.getFullYear(), d.getMonth()]; });
  const focusCell = (r: number, c: number) => {
    const el = gridRef.current?.querySelector<HTMLInputElement>(`[data-cell="${r}:${c}"]`);
    if (el) { el.focus(); el.select(); }
  };
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>, r: number, c: number) => {
    const mv: Record<string, [number, number]> = { Enter: [1, 0], ArrowDown: [1, 0], ArrowUp: [-1, 0] };
    if (c > 0) Object.assign(mv, { ArrowRight: [0, 1], ArrowLeft: [0, -1] });
    const d = mv[e.key];
    if (d) { e.preventDefault(); focusCell(r + d[0], c + d[1]); }
  };
  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>, r: number, c: number) => {
    const text = e.clipboardData.getData('text/plain');
    if (!/[\t\n]/.test(text.trim())) return; // одно значение — обычная вставка
    e.preventDefault();
    pasteBlock(text, r, c);
  };

  const total = (title: string) => days.reduce((s, d) => s + (cell(title, iso(y, m, d))?.target_qty ?? regular(title)?.target_qty ?? 0), 0);
  const inputCls = 'h-8 w-full bg-transparent px-1 text-right tabular-nums outline-none focus:bg-primary/10 focus:ring-1 focus:ring-primary disabled:cursor-default';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="icon-sm" variant="ghost" aria-label="Прошлый месяц" onClick={() => shift(-1)}><ChevronLeft className="size-4" /></Button>
        <span className="min-w-36 text-center font-medium">{MONTHS[m]} {y}</span>
        <Button size="icon-sm" variant="ghost" aria-label="Следующий месяц" onClick={() => shift(1)}><ChevronRight className="size-4" /></Button>
        <span className="ml-auto text-xs text-muted-foreground">Ячейка — сколько продать в этот день. Можно вставить блок из Excel прямо в таблицу.</span>
      </div>
      <div className="overflow-x-auto rounded-lg border">
        <table ref={gridRef} className="border-collapse text-sm">
          <thead>
            <tr className="bg-muted/50 text-xs text-muted-foreground">
              <th className="sticky left-0 z-10 min-w-44 border-b border-r bg-muted px-2 py-1 text-left font-medium">Позиция</th>
              <Tooltip content="Цель на каждый день месяца, если день не заполнен"><th className="border-b border-r px-1 py-1 font-medium">Обычно</th></Tooltip>
              {days.map((d) => {
                const wd = new Date(y, m, d).getDay();
                const today = iso(y, m, d) === todayIso;
                return (
                  <th key={d} className={cn('w-11 min-w-11 border-b border-r px-0.5 py-1 font-medium', (wd === 0 || wd === 6) && 'bg-muted', today && 'text-primary')}>
                    <div className="tabular-nums">{d}</div>
                    <div className="text-[10px] font-normal">{WD[wd]}</div>
                  </th>
                );
              })}
              <th className="border-b px-2 py-1 font-medium">Итого</th>
              <th className="border-b" />
            </tr>
          </thead>
          <tbody>
            {titles.map((title, ri) => (
              <tr key={ri} className="group">
                <td className="sticky left-0 z-10 border-b border-r bg-card px-1">
                  <TitleInput cell={`${ri}:0`} title={title} disabled={disabled} onCommit={(t) => rename(title, t)}
                    onKeyDown={(e) => onKey(e, ri, 0)} onPaste={(e) => onPaste(e, ri, 0)} />
                </td>
                <td className="border-b border-r bg-muted/20">
                  <input data-cell={`${ri}:1`} inputMode="numeric" className={inputCls} disabled={disabled} value={regular(title)?.target_qty ?? ''}
                    onChange={(e) => write([[title, null, num(e.target.value)]])} onKeyDown={(e) => onKey(e, ri, 1)} onPaste={(e) => onPaste(e, ri, 1)} />
                </td>
                {days.map((d) => {
                  const date = iso(y, m, d);
                  const v = cell(title, date)?.target_qty;
                  const wd = new Date(y, m, d).getDay();
                  return (
                    <td key={d} className={cn('border-b border-r', (wd === 0 || wd === 6) && 'bg-muted/30', date === todayIso && 'bg-primary/5')}>
                      <input data-cell={`${ri}:${d + 1}`} inputMode="numeric" className={inputCls} disabled={disabled}
                        value={v ?? ''} placeholder={regular(title)?.target_qty ? String(regular(title)!.target_qty) : ''}
                        onChange={(e) => write([[title, date, num(e.target.value)]])}
                        onKeyDown={(e) => onKey(e, ri, d + 1)} onPaste={(e) => onPaste(e, ri, d + 1)} />
                    </td>
                  );
                })}
                <td className="border-b px-2 text-right tabular-nums text-muted-foreground">{total(title) || ''}</td>
                <td className="border-b px-1">
                  {!disabled && (
                    <button type="button" aria-label="Удалить позицию" className="invisible text-muted-foreground hover:text-destructive group-hover:visible" onClick={() => removeTitle(title)}>
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {titles.length === 0 && (
              <tr><td colSpan={nDays + 4} className="px-3 py-6 text-center text-sm text-muted-foreground">Плана на этот месяц нет. Скопируйте таблицу из Excel и вставьте кнопкой ниже.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {pasteOpen && (
        <div className="space-y-2 rounded-lg border border-dashed p-3">
          <p className="text-xs text-muted-foreground">Строка — позиция и дальше количества по дням с 1-го числа, как скопировано из Excel: «Сырники⇥20⇥20⇥25…». Пустая ячейка — без цели в этот день. Шапку можно не убирать.</p>
          <Textarea rows={6} value={pasteText} onChange={(e) => setPasteText(e.target.value)} autoFocus placeholder={'Сырники\t20\t20\t25\nХот-доги\t15\t15\t15'} />
          <div className="flex gap-2">
            <Button size="sm" disabled={!pasteText.trim()} onClick={() => {
              // из Excel по дням: «Позиция ⇥ день1 ⇥ день2…» → колонки начиная с дня 1 (после «Обычно»)
              const shifted = pasteText.replace(/\r/g, '').split('\n').map((l) => { const [t, ...rest] = l.split('\t'); return [t, '', ...rest].join('\t'); }).join('\n');
              pasteBlock(shifted, titles.length, 0, true);
              setPasteText(''); setPasteOpen(false);
            }}>Вставить в {MONTHS[m].toLowerCase()}</Button>
            <Button size="sm" variant="ghost" onClick={() => { setPasteOpen(false); setPasteText(''); }}>Отмена</Button>
          </div>
        </div>
      )}
      {!disabled && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => {
            let t = 'Новая позиция', i = 2;
            while (titles.some((x) => norm(x) === norm(t))) t = `Новая позиция ${i++}`;
            setExtra((xs) => [...xs, t]);
            setTimeout(() => focusCell(titles.length, 0), 0);
          }}>
            <Plus className="mr-1 size-4" /> Позиция
          </Button>
          {!pasteOpen && (
            <Button size="sm" variant="ghost" onClick={() => setPasteOpen(true)}>
              <ClipboardPaste className="mr-1 size-4" /> Вставить из Excel
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** Название позиции правится локально и применяется на blur/Enter — иначе строка «пропадает» на пустом имени. */
function TitleInput({ cell, title, disabled, onCommit, onKeyDown, onPaste }: {
  cell: string; title: string; disabled: boolean; onCommit: (t: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void; onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
}) {
  const [v, setV] = useState(title);
  const [editing, setEditing] = useState(false);
  const commit = () => { setEditing(false); const t = v.trim(); if (t && t !== title) onCommit(t); else setV(title); };
  return (
    <input data-cell={cell} className="h-8 w-full bg-transparent px-1 outline-none focus:bg-primary/10" disabled={disabled}
      value={editing ? v : title} onFocus={() => { setV(title); setEditing(true); }} onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); onKeyDown(e); }} onPaste={onPaste} />
  );
}
